import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Project } from '../types';
import { cacheService, getSelectedProjectId, setSelectedProjectId } from '../services/cacheService';

interface ProjectContextType {
  selectedProject: Project | null;
  detailedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  setDetailedProject: (project: Project | null) => void;
  projects: Project[];
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [detailedProject, setDetailedProjectState] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, load cached projects and selected project ID
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const cachedProjects = await cacheService.getCachedProjects();
      setProjects(cachedProjects);
      const selectedId = await getSelectedProjectId();
      console.log('ProjectContext: Loading cached projects:', cachedProjects.length);
      console.log('ProjectContext: Selected project ID from cache:', selectedId);
      if (selectedId) {
        const found = cachedProjects.find(p => String(p.id) === String(selectedId));
        if (found) {
          console.log('ProjectContext: Found selected project in cache:', found);
          setSelectedProjectState(found);
          // Note: We don't load detailed project here to avoid performance impact
          // Detailed project should be loaded on-demand by the pages that need it
        } else {
          console.log('ProjectContext: Selected project not found in cached projects');
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  // When user selects a project, save its ID in IndexedDB
  const setSelectedProject = (project: Project | null) => {
    console.log('ProjectContext: setSelectedProject called with:', project);
    setSelectedProjectState(project);
    // Clear detailed project when changing projects
    if (project === null || project.id !== selectedProject?.id) {
      setDetailedProjectState(null);
    }
    setSelectedProjectId(project ? String(project.id) : null);
    console.log('ProjectContext: Project saved to cache with ID:', project ? String(project.id) : null);
  };

  // Set detailed project data
  const setDetailedProject = (project: Project | null) => {
    setDetailedProjectState(project);
  };

  // Debug: Monitor selectedProject changes
  useEffect(() => {
    console.log('ProjectContext: selectedProject state changed to:', selectedProject);
  }, [selectedProject]);

  return (
    <ProjectContext.Provider 
      value={{ 
        selectedProject, 
        detailedProject,
        setSelectedProject,
        setDetailedProject,
        projects, 
        loading 
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};